import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  MessageSquare,
  Search,
  Calendar,
  Link2,
  Trash2,
  Download,
  RefreshCcw,
  Inbox,
  Sparkles,
  CheckSquare,
  Square,
  ChevronDown,
  X,
  Loader2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOT_AVATAR =
  "https://customer-assets.emergentagent.com/job_346f0702-303d-4720-802e-8e6b3fce771c/artifacts/ggtde7lk_kling_20260517_Build_Avatar_Friendly_m_283_2.png";

const renderRich = (content) => {
  if (!content) return null;
  const pattern = /(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)/g;
  const nodes = [];
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = pattern.exec(content)) !== null) {
    if (m.index > lastIndex) nodes.push(content.slice(lastIndex, m.index));
    const tok = m[0];
    if (tok.startsWith("[")) {
      const lm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      nodes.push(
        lm ? (
          <a
            key={`l-${key++}`}
            href={lm[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-indigo-600 underline decoration-2 underline-offset-2"
          >
            {lm[1]}
          </a>
        ) : (
          tok
        )
      );
    } else {
      nodes.push(
        <strong key={`b-${key++}`} className="font-bold">
          {tok.slice(2, -2)}
        </strong>
      );
    }
    lastIndex = m.index + tok.length;
  }
  if (lastIndex < content.length) nodes.push(content.slice(lastIndex));
  return nodes;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatRel = (iso) => {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return "gerade eben";
    if (m < 60) return `vor ${m} Min.`;
    const h = Math.floor(m / 60);
    if (h < 24) return `vor ${h} Std.`;
    const d = Math.floor(h / 24);
    if (d < 30) return `vor ${d} Tag${d === 1 ? "" : "en"}`;
    return formatDate(iso);
  } catch {
    return "";
  }
};

// Lightweight markdown renderer for analysis output (## headings + ** bold + - lists)
const renderAnalysisMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let key = 0;
  let listBuffer = [];

  const flushList = () => {
    if (listBuffer.length === 0) return;
    out.push(
      <ul key={`ul-${key++}`} className="list-disc pl-6 space-y-1 my-2 text-slate-700">
        {listBuffer.map((it, i) => (
          <li key={i}>{renderInline(it)}</li>
        ))}
      </ul>
    );
    listBuffer = [];
  };

  const renderInline = (s) => {
    const parts = [];
    let last = 0;
    let mk = 0;
    const re = /\*\*([^*]+)\*\*/g;
    let mm;
    while ((mm = re.exec(s)) !== null) {
      if (mm.index > last) parts.push(s.slice(last, mm.index));
      parts.push(
        <strong key={`b-${mk++}`} className="font-bold text-slate-900">
          {mm[1]}
        </strong>
      );
      last = mm.index + mm[0].length;
    }
    if (last < s.length) parts.push(s.slice(last));
    return parts;
  };

  for (const line of lines) {
    if (line.startsWith("## ")) {
      flushList();
      out.push(
        <h3 key={`h-${key++}`} className="text-base font-bold text-slate-900 mt-5 mb-2 flex items-center gap-2">
          {renderInline(line.replace(/^##\s+/, ""))}
        </h3>
      );
    } else if (/^\s*\d+\.\s/.test(line)) {
      flushList();
      out.push(
        <p key={`n-${key++}`} className="text-sm text-slate-700 my-1.5 pl-1">
          {renderInline(line)}
        </p>
      );
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      listBuffer.push(line.replace(/^[-*]\s+/, ""));
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      out.push(
        <p key={`p-${key++}`} className="text-sm text-slate-700 my-1.5 leading-relaxed">
          {renderInline(line)}
        </p>
      );
    }
  }
  flushList();
  return out;
};

const SessionRow = ({ session, isActive, isSelected, onToggleSelect, onClick }) => (
  <div
    className={`w-full border-b border-slate-100 transition-colors ${
      isActive
        ? "bg-indigo-50 border-l-4 border-l-indigo-600"
        : isSelected
        ? "bg-indigo-50/40 border-l-4 border-l-indigo-300"
        : "hover:bg-slate-50 border-l-4 border-l-transparent"
    }`}
  >
    <div className="flex items-start gap-2 px-3 py-3">
      <div
        className="pt-0.5 flex-shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(session.id);
        }}
        data-testid={`hypeconnect-session-checkbox-${session.id}`}
      >
        <Checkbox checked={isSelected} className="cursor-pointer" />
      </div>
      <button
        onClick={onClick}
        className="flex-1 text-left min-w-0"
        data-testid={`hypeconnect-session-row-${session.id}`}
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <span className="text-sm font-semibold text-slate-900 truncate">
            {session.customer_name || "Anonym"}
          </span>
          <span className="text-[10px] text-slate-400 whitespace-nowrap">
            {formatRel(session.updated_at || session.created_at)}
          </span>
        </div>
        <p className="text-xs text-slate-600 line-clamp-2 mb-2">
          {session.first_user_message || (
            <span className="italic text-slate-400">Noch keine Nutzer-Frage</span>
          )}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-5">
            <MessageSquare className="w-3 h-3 mr-1" />
            {session.message_count}
          </Badge>
          {session.user_message_count > 0 && (
            <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-5 bg-slate-100">
              {session.user_message_count} Fragen
            </Badge>
          )}
          {session.has_cta_link && (
            <Badge className="text-[10px] py-0 px-1.5 h-5 bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
              <Link2 className="w-3 h-3 mr-1" />
              CTA
            </Badge>
          )}
        </div>
      </button>
    </div>
  </div>
);

const ChatBubble = ({ msg, idx }) => {
  const isBot = msg.role === "assistant";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex items-start gap-2 ${isBot ? "" : "flex-row-reverse"}`}
      data-testid={`detail-msg-${msg.role}-${idx}`}
    >
      {isBot && (
        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-sm">
          <img
            src={BOT_AVATAR}
            alt="Lisa"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 18%" }}
          />
        </div>
      )}
      <div
        className={`max-w-[78%] px-3 py-2 shadow-sm text-sm ${
          isBot
            ? "bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-tl-sm"
            : "bg-gradient-to-br from-indigo-600 to-violet-600 text-white rounded-2xl rounded-tr-sm"
        }`}
      >
        <p className="leading-relaxed whitespace-pre-wrap">{renderRich(msg.content)}</p>
      </div>
    </motion.div>
  );
};

const Detail = ({ session, onDelete, onExport }) => {
  if (!session) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-400 px-8 text-center">
        <Inbox className="w-12 h-12 mb-3" />
        <p className="text-sm font-medium">Wählen Sie links eine Session aus</p>
        <p className="text-xs mt-1">um den vollständigen Chat-Verlauf zu sehen.</p>
      </div>
    );
  }
  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-bold text-slate-900 truncate">
              {session.customer_name || "Anonym"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(session.created_at)}
              </span>
              <span>·</span>
              <span>{session.messages?.length || 0} Nachrichten</span>
              <span>·</span>
              <span className="font-mono text-[10px] text-slate-400">{session.id?.slice(0, 8)}</span>
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={onExport} data-testid="hypeconnect-detail-export">
              <Download className="w-3.5 h-3.5 mr-1" />
              TXT
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onDelete}
              className="text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
              data-testid="hypeconnect-detail-delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
      <ScrollArea className="flex-1 bg-slate-50/40">
        <div className="p-6 space-y-3 max-w-3xl mx-auto">
          {(session.messages || []).map((m, i) => (
            <ChatBubble key={i} msg={m} idx={i} />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

// Bulk-Delete Dropdown
const BulkDeleteMenu = ({ onPick, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const options = [
    { v: 10, l: "Letzte 10" },
    { v: 20, l: "Letzte 20" },
    { v: 50, l: "Letzte 50" },
    { v: 100, l: "Letzte 100" },
    { v: "all", l: "Alle Sessions" },
  ];
  return (
    <div className="relative" ref={ref}>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(!open)}
        disabled={disabled}
        className="text-red-600 hover:bg-red-50 border-red-200"
        data-testid="hypeconnect-bulk-delete-menu"
      >
        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
        Massenlöschen
        <ChevronDown className="w-3.5 h-3.5 ml-1" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl border border-slate-200 z-20 overflow-hidden">
          {options.map((o) => (
            <button
              key={o.v}
              onClick={() => {
                setOpen(false);
                onPick(o.v);
              }}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-colors"
              data-testid={`hypeconnect-bulk-delete-${o.v}`}
            >
              {o.l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Analyse-Modal
const AnalysisModal = ({ open, onClose, isLoading, result, error }) => {
  if (!open) return null;
  const handleExportMd = () => {
    if (!result?.analysis) return;
    const blob = new Blob([result.analysis], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hypeconnect-analyse-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = () => {
    if (!result?.analysis) return;
    // Render einfaches Markdown -> HTML für den Druck-/PDF-Export
    const renderInlineHtml = (s) =>
      (s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

    const lines = result.analysis.split("\n");
    const htmlParts = [];
    let inList = false;
    const closeList = () => {
      if (inList) {
        htmlParts.push("</ul>");
        inList = false;
      }
    };
    for (const line of lines) {
      if (line.startsWith("## ")) {
        closeList();
        htmlParts.push(`<h2>${renderInlineHtml(line.replace(/^##\s+/, ""))}</h2>`);
      } else if (/^\s*\d+\.\s/.test(line)) {
        closeList();
        htmlParts.push(`<p>${renderInlineHtml(line)}</p>`);
      } else if (line.startsWith("- ") || line.startsWith("* ")) {
        if (!inList) {
          htmlParts.push("<ul>");
          inList = true;
        }
        htmlParts.push(`<li>${renderInlineHtml(line.replace(/^[-*]\s+/, ""))}</li>`);
      } else if (line.trim() === "") {
        closeList();
      } else {
        closeList();
        htmlParts.push(`<p>${renderInlineHtml(line)}</p>`);
      }
    }
    closeList();

    const date = new Date().toLocaleString("de-DE");
    const stats = result.stats || {};
    const html = `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8" />
<title>HypeConnect KI-Auswertung – ${date}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #0f172a; line-height: 1.55; padding: 32px; max-width: 760px; margin: auto; }
  h1 { font-size: 22px; margin: 0 0 6px; color: #4f46e5; }
  .subtitle { font-size: 12px; color: #64748b; margin-bottom: 18px; }
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 22px; padding: 12px; background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 10px; }
  .stat { text-align: center; }
  .stat .num { font-size: 22px; font-weight: 700; color: #4338ca; }
  .stat .lbl { font-size: 10px; text-transform: uppercase; color: #64748b; letter-spacing: 0.5px; }
  h2 { font-size: 15px; color: #1e293b; margin: 18px 0 6px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; }
  p { font-size: 13px; margin: 6px 0; }
  ul { margin: 4px 0 8px 0; padding-left: 20px; }
  li { font-size: 13px; margin: 2px 0; }
  strong { color: #0f172a; }
  .footer { margin-top: 30px; font-size: 10px; color: #94a3b8; text-align: center; }
  @media print { body { padding: 16px; } }
</style>
</head>
<body>
  <h1>HypeConnect – KI-Auswertung der Chats</h1>
  <div class="subtitle">Generiert am ${date} · Powered by GPT-4o</div>
  <div class="stats">
    <div class="stat"><div class="num">${result.session_count || 0}</div><div class="lbl">Chats analysiert</div></div>
    <div class="stat"><div class="num">${stats.price_concerns || 0}</div><div class="lbl">Preis-Bedenken</div></div>
    <div class="stat"><div class="num">${stats.cta_sent || 0}</div><div class="lbl">CTA-Links</div></div>
    <div class="stat"><div class="num">${stats.abandoned || 0}</div><div class="lbl">Abgesprungen</div></div>
  </div>
  ${htmlParts.join("\n")}
  <div class="footer">HypeConnect Backoffice – vertraulich</div>
</body>
</html>`;

    const w = window.open("", "_blank", "noopener,noreferrer,width=900,height=900");
    if (!w) {
      toast.error("Bitte erlauben Sie Pop-ups, um die PDF-Vorschau zu öffnen.");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Print-Dialog automatisch öffnen, sobald geladen
    w.onload = () => {
      setTimeout(() => {
        try {
          w.focus();
          w.print();
        } catch (e) {
          /* ignore */
        }
      }, 250);
    };
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        data-testid="hypeconnect-analysis-modal"
      >
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-violet-50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-md flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-slate-900">KI-Auswertung der Chats</h3>
              <p className="text-xs text-slate-500 truncate">
                Powered by GPT-4o · Top-Themen, Einwände, Wissenslücken & Empfehlungen
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {result?.analysis && (
              <>
                <Button size="sm" variant="outline" onClick={handleExportPdf} data-testid="hypeconnect-analysis-pdf">
                  <Download className="w-3.5 h-3.5 mr-1" />
                  PDF
                </Button>
                <Button size="sm" variant="outline" onClick={handleExportMd}>
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Markdown
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="hypeconnect-analysis-close">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Scrollbarer Inhalt – native overflow für maximale Kompatibilität */}
        <div className="flex-1 overflow-y-auto min-h-0" data-testid="hypeconnect-analysis-scroll">
          <div className="p-6">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-3" />
                <p className="text-sm font-medium">Analysiere Chats mit GPT-4o…</p>
                <p className="text-xs text-slate-400 mt-1">Das dauert 15-45 Sekunden.</p>
              </div>
            )}
            {error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-4">
                <strong>Fehler:</strong> {error}
              </div>
            )}
            {result?.analysis && (
              <>
                <div className="bg-indigo-50/50 border border-indigo-200 rounded-xl p-3 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-sm">
                  <div>
                    <div className="text-2xl font-bold text-indigo-700">{result.session_count}</div>
                    <div className="text-[11px] text-slate-500">Chats analysiert</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-indigo-700">{result.stats?.price_concerns || 0}</div>
                    <div className="text-[11px] text-slate-500">Preis-Bedenken</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-indigo-700">{result.stats?.cta_sent || 0}</div>
                    <div className="text-[11px] text-slate-500">CTA-Links gesendet</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-indigo-700">{result.stats?.abandoned || 0}</div>
                    <div className="text-[11px] text-slate-500">Abgesprungen</div>
                  </div>
                </div>
                <div className="prose prose-sm max-w-none" data-testid="hypeconnect-analysis-content">
                  {renderAnalysisMarkdown(result.analysis)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminHypeConnectChats({ authToken }) {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [days, setDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${authToken}` } }),
    [authToken]
  );

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (days > 0) params.days = days;
      const res = await axios.get(`${API}/admin/hypeconnect-sessions`, {
        ...authHeader,
        params,
      });
      setSessions(res.data.sessions || []);
    } catch (e) {
      console.error("Fehler beim Laden der Sessions:", e);
    } finally {
      setLoading(false);
    }
  }, [authHeader, search, days]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleSelect = async (sessionId) => {
    setActiveId(sessionId);
    setActiveDetail(null);
    try {
      const res = await axios.get(
        `${API}/admin/hypeconnect-sessions/${sessionId}`,
        authHeader
      );
      setActiveDetail(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sessions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sessions.map((s) => s.id)));
    }
  };

  const handleDelete = async () => {
    if (!activeDetail) return;
    if (!window.confirm("Diese Session wirklich löschen?")) return;
    try {
      await axios.delete(
        `${API}/admin/hypeconnect-sessions/${activeDetail.id}`,
        authHeader
      );
      setActiveDetail(null);
      setActiveId(null);
      setSelectedIds((prev) => {
        const n = new Set(prev);
        n.delete(activeDetail.id);
        return n;
      });
      await loadList();
      toast.success("Session gelöscht");
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const handleBulkDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} ausgewählte Sessions wirklich löschen?`)) return;
    try {
      const res = await axios.post(
        `${API}/admin/hypeconnect-sessions/bulk-delete`,
        { session_ids: Array.from(selectedIds) },
        authHeader
      );
      toast.success(`${res.data.deleted} Sessions gelöscht`);
      setSelectedIds(new Set());
      setActiveId(null);
      setActiveDetail(null);
      await loadList();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const handleBulkDeleteByCount = async (count) => {
    const label = count === "all" ? "ALLE Sessions" : `die letzten ${count} Sessions`;
    if (!window.confirm(`${label} wirklich löschen? Diese Aktion ist nicht rückgängig zu machen.`)) return;
    try {
      const body = count === "all" ? { delete_all: true } : { last_n: count };
      const res = await axios.post(
        `${API}/admin/hypeconnect-sessions/bulk-delete`,
        body,
        authHeader
      );
      toast.success(`${res.data.deleted} Sessions gelöscht`);
      setSelectedIds(new Set());
      setActiveId(null);
      setActiveDetail(null);
      await loadList();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const handleExport = () => {
    if (!activeDetail) return;
    const lines = [
      `HypeConnect Chat – ${activeDetail.customer_name || "Anonym"}`,
      `Session-ID: ${activeDetail.id}`,
      `Erstellt: ${formatDate(activeDetail.created_at)}`,
      `Nachrichten: ${activeDetail.messages?.length || 0}`,
      "─".repeat(60),
      "",
    ];
    (activeDetail.messages || []).forEach((m) => {
      const who = m.role === "assistant" ? "Lisa" : (activeDetail.customer_name || "User");
      lines.push(`[${who}]`);
      lines.push(m.content || "");
      lines.push("");
    });
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hypeconnect-chat-${activeDetail.id.slice(0, 8)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAnalyze = async (mode) => {
    setAnalysisOpen(true);
    setAnalysisLoading(true);
    setAnalysisResult(null);
    setAnalysisError(null);
    try {
      const body =
        mode === "selected"
          ? { session_ids: Array.from(selectedIds) }
          : { analyze_all: true };
      const res = await axios.post(
        `${API}/admin/hypeconnect-sessions/analyze`,
        body,
        { ...authHeader, timeout: 120000 }
      );
      setAnalysisResult(res.data);
    } catch (e) {
      setAnalysisError(e.response?.data?.detail || e.message || "Unbekannter Fehler");
    } finally {
      setAnalysisLoading(false);
    }
  };

  const allSelected = sessions.length > 0 && selectedIds.size === sessions.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < sessions.length;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden h-[calc(100vh-180px)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-violet-50/50">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-600" />
              HypeConnect Chat-Verläufe
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {sessions.length} Session{sessions.length === 1 ? "" : "s"} – sehen Sie, was Kunden fragen und wie Lisa antwortet.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => handleAnalyze("all")}
              disabled={sessions.length === 0}
              className="bg-gradient-to-br from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-sm"
              data-testid="hypeconnect-analyze-all"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Alle auswerten
            </Button>
            <BulkDeleteMenu onPick={handleBulkDeleteByCount} disabled={sessions.length === 0} />
            <Button size="sm" variant="outline" onClick={loadList} data-testid="hypeconnect-refresh">
              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
              Aktualisieren
            </Button>
          </div>
        </div>
      </div>

      {/* Selection toolbar */}
      {selectedIds.size > 0 && (
        <div className="px-6 py-2.5 bg-indigo-600 text-white flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm font-medium" data-testid="hypeconnect-selected-count">
            {selectedIds.size} Session{selectedIds.size === 1 ? "" : "s"} ausgewählt
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => handleAnalyze("selected")}
              className="bg-white text-indigo-700 hover:bg-indigo-50"
              data-testid="hypeconnect-analyze-selected"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Auswahl auswerten
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkDeleteSelected}
              className="bg-white text-red-600 border-white hover:bg-red-50 hover:text-red-700"
              data-testid="hypeconnect-delete-selected"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Auswahl löschen
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
              className="text-white hover:bg-white/10"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="w-[360px] flex-shrink-0 border-r border-slate-200 flex flex-col bg-white">
          <div className="p-3 space-y-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Suchen…"
                className="pl-9 text-sm"
                data-testid="hypeconnect-search-input"
              />
            </div>
            <div className="flex gap-1">
              {[
                { v: 0, l: "Alle" },
                { v: 1, l: "Heute" },
                { v: 7, l: "7 Tage" },
                { v: 30, l: "30 Tage" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setDays(opt.v)}
                  data-testid={`hypeconnect-filter-${opt.v}`}
                  className={`flex-1 text-xs py-1 rounded-md transition-colors ${
                    days === opt.v
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            {sessions.length > 0 && (
              <button
                onClick={toggleSelectAll}
                className="w-full text-xs text-slate-600 hover:text-indigo-600 flex items-center gap-1.5 py-1"
                data-testid="hypeconnect-select-all"
              >
                {allSelected ? (
                  <CheckSquare className="w-3.5 h-3.5 text-indigo-600" />
                ) : someSelected ? (
                  <Square className="w-3.5 h-3.5 text-indigo-600 fill-indigo-200" />
                ) : (
                  <Square className="w-3.5 h-3.5" />
                )}
                {allSelected
                  ? "Auswahl aufheben"
                  : someSelected
                  ? `${selectedIds.size} ausgewählt – alle wählen?`
                  : "Alle wählen"}
              </button>
            )}
          </div>

          <ScrollArea className="flex-1">
            {loading ? (
              <div className="p-6 text-center text-slate-400 text-sm">Lade…</div>
            ) : sessions.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">
                <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                Keine Sessions gefunden.
              </div>
            ) : (
              sessions.map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  isActive={s.id === activeId}
                  isSelected={selectedIds.has(s.id)}
                  onToggleSelect={toggleSelect}
                  onClick={() => handleSelect(s.id)}
                />
              ))
            )}
          </ScrollArea>
        </div>

        <div className="flex-1 min-w-0 bg-slate-50/30">
          <Detail session={activeDetail} onDelete={handleDelete} onExport={handleExport} />
        </div>
      </div>

      <AnalysisModal
        open={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        isLoading={analysisLoading}
        result={analysisResult}
        error={analysisError}
      />
    </div>
  );
}
